import type { SpeakingResults } from '@ielts-buddy/shared'
import { useEffect, useRef, useState } from 'react'
import { t } from '../../i18n/messages.js'
import { fetchSpeakingResults, submitFeedback } from '../../shared/api.js'
import { usePreferencesStore } from '../../shared/preferences-store.js'
import { useRouter } from '../../shared/router-store.js'
import { haptics } from '../../shared/telegram.js'
import {
  ArrowLeftIcon,
  CheckIcon,
  ChevronDownIcon,
  HomeIcon,
  InfoIcon,
  RefreshIcon,
  SparkleIcon,
  TargetIcon,
} from '../../shared/ui/icons.js'
import {
  Button,
  Card,
  ErrorState,
  FeedbackPrompt,
  IconButton,
  LoadingDots,
  ScreenHeader,
  ScreenHeaderSpacer,
  shouldShowFeedback,
} from '../../shared/ui/index.js'
import { useCountUp } from '../../shared/use-count-up.js'
import {
  getBandDescriptor,
  toneFillClass,
  toneTextClass,
} from './band-descriptor.js'

interface Props {
  sessionId: string
}

/**
 * Speaking results screen.
 *
 * The "hero" is the band score, rendered as a big circular ring (Apple
 * Fitness-style) tinted by the band's IELTS tone (Expert/Strong/Good/Fair/Weak).
 * Below it: the official IELTS descriptor (e.g. "8.5 — Very Good"), a per-
 * criterion breakdown, qualitative summary, strengths, improvements, expandable
 * transcript, and finally action buttons that invite the user to practice again.
 *
 * Design priorities:
 *  - The score is the moment — make it large, beautiful, and contextual.
 *  - Descriptor + blurb give the score immediate meaning without forcing
 *    the user to look up the IELTS scale.
 *  - Strengths and improvements are equally weighted (left card / right card
 *    on wide screens, stacked on mobile) so users don't only see what they
 *    did wrong.
 *  - "Practice again" is the primary CTA — turning a results screen into the
 *    start of the next session is the single biggest engagement win.
 */
export function SpeakingResultsScreen({ sessionId }: Props) {
  const locale = usePreferencesStore((s) => s.locale)
  const navigateHome = useRouter((s) => s.navigateHome)
  const navigateToSpeaking = useRouter((s) => s.navigateToSpeaking)
  const [results, setResults] = useState<SpeakingResults | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showTranscript, setShowTranscript] = useState(false)
  const [showFeedback, setShowFeedback] = useState(false)
  const fetchedRef = useRef<string | null>(null)

  useEffect(() => {
    if (fetchedRef.current === sessionId) return
    fetchedRef.current = sessionId

    ;(async () => {
      try {
        const r = await fetchSpeakingResults(sessionId)
        setResults(r)
        // Delay the feedback prompt slightly so the user can absorb their score
        // before being asked anything. Only show if cooldown has elapsed.
        if (shouldShowFeedback()) {
          setTimeout(() => setShowFeedback(true), 1500)
        }
      } catch (err) {
        setError((err as Error).message)
      }
    })()
  }, [sessionId])

  if (error) {
    return (
      <Centered>
        <ErrorState
          title={t(locale, 'results.detailLoadError')}
          message={error}
          primaryAction={{
            label: t(locale, 'results.backHome'),
            onClick: navigateHome,
          }}
        />
      </Centered>
    )
  }

  if (!results) {
    return (
      <Centered>
        <LoadingDots label={t(locale, 'results.detailLoading')} />
      </Centered>
    )
  }

  const {
    overallBand,
    criteria,
    summary,
    strengths,
    improvements,
    transcript,
  } = results
  const descriptor = getBandDescriptor(overallBand ?? 0)

  // How many turns in this session were "skipped" via the dev /skip-turn
  // endpoint? Skipped turns are scored against a server-side canned answer,
  // so the band score reflects those placeholders — NOT what the candidate
  // actually said. Be honest about this in the UI when it applies, otherwise
  // a tester sees "Band 5.0" after pressing Skip 14 times and reasonably
  // wonders where it came from.
  const skippedCount = transcript.filter((t) => t.skipped).length
  const totalCount = transcript.length
  const allSkipped = totalCount > 0 && skippedCount === totalCount
  const someSkipped = skippedCount > 0

  return (
    <div className="min-h-full screen-padding flex flex-col gap-5 animate-fade-in">
      <ScreenHeader
        leading={
          <IconButton
            aria-label={t(locale, 'results.backHome')}
            onClick={() => {
              haptics.light()
              navigateHome()
            }}
          >
            <ArrowLeftIcon className="w-4 h-4" />
          </IconButton>
        }
        kicker={t(locale, 'results.title')}
        trailing={<ScreenHeaderSpacer />}
      />

      {someSkipped && (
        <SkippedBanner
          skippedCount={skippedCount}
          totalCount={totalCount}
          allSkipped={allSkipped}
        />
      )}

      {/* Hero score — gradient treatment matches the Home modules and the
          Speaking cue card. The score IS the moment; it earns the heaviest
          visual weight on the screen. */}
      <Card size="hero" tone="gradient" className="p-6 text-center">
        <BandRing band={overallBand} />
        <div className="mt-4 inline-flex items-center px-3 py-1 rounded-full bg-white/20 text-[11px] uppercase tracking-wider font-semibold">
          {descriptor.label}
        </div>
        <p className="mt-3 text-sm text-white/80 leading-relaxed max-w-sm mx-auto">
          {descriptor.blurb}
        </p>
      </Card>

      {/* Criteria breakdown */}
      {criteria &&
        (() => {
          const lowestKey = (
            [
              ['fluency_coherence', criteria.fluency_coherence],
              ['lexical_resource', criteria.lexical_resource],
              [
                'grammatical_range_accuracy',
                criteria.grammatical_range_accuracy,
              ],
              ['pronunciation', criteria.pronunciation],
            ] as [string, number][]
          ).reduce((a, b) => (a[1] <= b[1] ? a : b))[0]
          return (
            <Card className="p-5">
              <h2 className="text-xs uppercase tracking-wider text-brand-text-muted font-semibold mb-4">
                Band breakdown
              </h2>
              <ul className="space-y-3.5">
                <CriterionBar
                  label="Fluency & Coherence"
                  value={criteria.fluency_coherence}
                  isLowest={lowestKey === 'fluency_coherence'}
                />
                <CriterionBar
                  label="Lexical Resource"
                  value={criteria.lexical_resource}
                  isLowest={lowestKey === 'lexical_resource'}
                />
                <CriterionBar
                  label="Grammatical Range"
                  value={criteria.grammatical_range_accuracy}
                  isLowest={lowestKey === 'grammatical_range_accuracy'}
                />
                <CriterionBar
                  label="Pronunciation"
                  value={criteria.pronunciation}
                  hint="Estimated from transcript only"
                  isLowest={lowestKey === 'pronunciation'}
                />
              </ul>
            </Card>
          )
        })()}

      {/* Summary — same elevation as the criteria card so the tinted
          tone reads as "informational variant" instead of "less important". */}
      {summary && (
        <Card tone="info-soft" className="p-5">
          <div className="flex items-start gap-3">
            <SparkleIcon className="w-5 h-5 text-brand-info flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-xs uppercase tracking-wider text-brand-info font-semibold mb-1">
                Examiner's summary
              </h2>
              <p className="text-sm leading-relaxed text-brand-text">
                {summary}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Strengths & improvements — paired cards */}
      {(strengths.length > 0 || improvements.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {strengths.length > 0 && (
            <FeedbackCard
              title="What you did well"
              items={strengths}
              icon={<CheckIcon className="w-4 h-4" />}
              tone="primary-soft"
              accent="text-brand-primary"
            />
          )}
          {improvements.length > 0 && (
            <FeedbackCard
              title="Focus on improving"
              items={improvements}
              icon={<TargetIcon className="w-4 h-4" />}
              tone="warning-soft"
              accent="text-brand-warning"
            />
          )}
        </div>
      )}

      {/* Transcript (collapsed by default) */}
      <Card className="p-4">
        <button
          onClick={() => {
            haptics.select()
            setShowTranscript((v) => !v)
          }}
          className="w-full flex items-center justify-between text-sm font-medium text-brand-text"
          aria-expanded={showTranscript}
        >
          <span>Transcript ({transcript.length} turns)</span>
          <ChevronDownIcon
            className={`w-4 h-4 text-brand-text-muted transition-transform ${
              showTranscript ? 'rotate-180' : ''
            }`}
          />
        </button>
        {showTranscript && (
          <ol className="mt-4 space-y-4 text-sm">
            {transcript.map((t) => {
              // Three mutually exclusive states for what to render under the
              // examiner's question:
              //  1. real answer   → quote the transcript
              //  2. skipped (dev) → "No answer recorded — placeholder…"
              //  3. neither       → "No answer recorded" (defensive: shouldn't
              //                     happen in normal use now that we drop the
              //                     outro server-side, but kept so a tester
              //                     never sees a question with nothing under it)
              const hasAnswer = !!t.candidateTranscript && !t.skipped
              return (
                <li
                  key={t.position}
                  className="border-l-2 border-brand-border pl-3"
                >
                  <div className="text-[11px] uppercase tracking-wider text-brand-text-muted font-semibold mb-1 flex items-center gap-2">
                    <span>Part {t.part}</span>
                    {t.skipped && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-brand-bg-soft border border-brand-border text-brand-text-muted">
                        Skipped
                      </span>
                    )}
                  </div>
                  <div className="text-brand-primary mb-1 leading-relaxed">
                    {t.examinerText}
                  </div>
                  {hasAnswer ? (
                    <div className="text-brand-text leading-relaxed">
                      "{t.candidateTranscript}"
                    </div>
                  ) : t.skipped ? (
                    <div className="text-brand-text-subtle italic text-xs">
                      No answer recorded — placeholder used for testing
                    </div>
                  ) : (
                    <div className="text-brand-text-subtle italic text-xs">
                      No answer recorded
                    </div>
                  )}
                </li>
              )
            })}
          </ol>
        )}
      </Card>

      {showFeedback && (
        <FeedbackPrompt
          onSubmit={({ rating, comment }) =>
            submitFeedback({
              rating,
              comment,
              sessionId,
              context: 'post_session',
            })
          }
          onDismiss={() => setShowFeedback(false)}
        />
      )}

      {/* Action buttons.
          Leading icons reinforce the verb: a refresh arrow for "Practice
          again" reads as "do it once more", and a house glyph for "Back to
          home" leaves no doubt about where you'll land. Both are stroke
          icons so they sit comfortably on top of the gradient and outlined
          surfaces respectively. */}
      <div className="flex flex-col gap-2 mt-2">
        <Button
          onClick={() => navigateToSpeaking()}
          leadingIcon={<RefreshIcon className="w-4 h-4" />}
        >
          Practice again
        </Button>
        <Button
          variant="secondary"
          onClick={() => navigateHome()}
          leadingIcon={<HomeIcon className="w-4 h-4" />}
        >
          Back to home
        </Button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Hero band ring
// ---------------------------------------------------------------------------

/**
 * Big circular ring (Apple Fitness-style) that displays the overall band.
 *
 * Designed to sit on the gradient hero card: the track and stroke are both
 * white-with-opacity so the gradient stays the visual anchor and the ring
 * reads as a "fill against frosted glass". The tonal feedback (Expert /
 * Strong / Good / Fair / Weak) lives in the pill below the ring instead
 * of being baked into the stroke colour, which keeps the ring legible
 * against any band-tone gradient variation we might introduce later.
 */
function BandRing({ band }: { band: number | null }) {
  const radius = 64
  const stroke = 10
  const size = (radius + stroke) * 2
  const circumference = 2 * Math.PI * radius
  const fraction = band !== null ? Math.min(1, Math.max(0, band / 9)) : 0
  const dashOffset = circumference * (1 - fraction)
  // Count the number up alongside the ring fill — same easing, same duration.
  // Without this the number snaps in at t=0 while the ring is still climbing,
  // and the "moment" loses half its punch.
  const animatedBand = useCountUp(band)

  return (
    <div
      className="relative mx-auto"
      style={{ width: size, height: size }}
      aria-label={
        band !== null
          ? `Overall band ${band.toFixed(1)} out of 9`
          : 'No band score yet'
      }
    >
      <svg width={size} height={size} className="-rotate-90 absolute inset-0">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.18)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#ffffff"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          style={{
            transition: 'stroke-dashoffset 1.2s cubic-bezier(0.2, 0.8, 0.2, 1)',
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-brand-on-primary">
        <div className="text-[11px] uppercase tracking-wider text-white/70 font-semibold">
          Overall
        </div>
        <div className="font-sans font-bold text-6xl leading-none mt-1 text-white tabular-nums">
          {band !== null ? animatedBand.toFixed(1) : '—'}
        </div>
        <div className="text-[11px] text-white/60 mt-0.5">out of 9.0</div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Criterion bar — labelled, animated, tone-coloured
// ---------------------------------------------------------------------------

function CriterionBar({
  label,
  value,
  hint,
  isLowest = false,
}: {
  label: string
  value: number
  hint?: string
  isLowest?: boolean
}) {
  const pct = Math.min(100, Math.max(0, Math.round((value / 9) * 100)))
  const tone = getBandDescriptor(value).tone
  const fill = toneFillClass(tone)
  return (
    <li>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-sm text-brand-text flex items-center gap-1.5">
          {label}
          {isLowest && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-brand-warning-soft text-brand-warning border border-brand-warning/25 font-semibold leading-none">
              Focus
            </span>
          )}
        </span>
        <span
          className={`text-sm font-semibold tabular-nums ${toneTextClass(tone)}`}
        >
          {value.toFixed(1)}
        </span>
      </div>
      <div className="h-2 bg-brand-bg-soft rounded-full overflow-hidden">
        <div
          className={`h-full w-full ${fill} rounded-full origin-left`}
          style={{
            transform: `scaleX(${pct / 100})`,
            transition: 'transform 1s cubic-bezier(0.2, 0.8, 0.2, 1)',
          }}
        />
      </div>
      {hint && (
        <div className="text-[11px] text-brand-text-subtle mt-1.5 italic">
          {hint}
        </div>
      )}
    </li>
  )
}

// ---------------------------------------------------------------------------
// Feedback card (used for both strengths and improvements)
// ---------------------------------------------------------------------------

function FeedbackCard({
  title,
  items,
  icon,
  tone,
  accent,
}: {
  title: string
  items: string[]
  icon: React.ReactNode
  tone: 'primary-soft' | 'warning-soft'
  accent: string
}) {
  return (
    <Card tone={tone} className="p-4 h-full">
      <h2
        className={`text-xs uppercase tracking-wider font-semibold mb-2 flex items-center gap-1.5 ${accent}`}
      >
        {icon}
        {title}
      </h2>
      <ul className="text-sm space-y-1.5">
        {items.map((s, i) => (
          <li key={i} className="flex gap-2 text-brand-text leading-relaxed">
            <span aria-hidden className={`${accent} font-bold flex-shrink-0`}>
              ·
            </span>
            <span>{s}</span>
          </li>
        ))}
      </ul>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Dev-skip banner — tells the user that some/all of their "answers" were
// canned placeholders inserted by the dev /skip-turn endpoint, so the band
// score reflects those, not anything they actually said. Without this, a
// tester (or the developer) sees a Band 5.0 after pressing Skip 14 times
// and rightfully wonders where the score came from.
// ---------------------------------------------------------------------------

function SkippedBanner({
  skippedCount,
  totalCount,
  allSkipped,
}: {
  skippedCount: number
  totalCount: number
  allSkipped: boolean
}) {
  return (
    <Card
      size="flat"
      tone="warning-soft"
      className="p-4 flex gap-3 items-start"
      as="aside"
    >
      <InfoIcon className="w-5 h-5 text-brand-warning flex-shrink-0 mt-0.5" />
      <div className="text-xs leading-relaxed text-brand-text">
        <div className="font-semibold text-brand-warning uppercase tracking-wider text-[11px] mb-1">
          Dev mode · skipped answers
        </div>
        {allSkipped ? (
          <p>
            All <strong>{skippedCount}</strong> of {totalCount} turns were
            skipped, so the scores below are calculated from placeholder
            "candidate" answers — not anything you said. They're useful for
            testing the scoring pipeline end-to-end, not for measuring real
            ability.
          </p>
        ) : (
          <p>
            <strong>{skippedCount}</strong> of {totalCount} turns were skipped.
            Skipped turns are scored against canned placeholder answers (look
            for the "Skipped" tag in the transcript), so the overall band
            reflects a mix of your real answers and the placeholders.
          </p>
        )}
      </div>
    </Card>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-full flex items-center justify-center p-6">
      {children}
    </div>
  )
}
